import {
	Button,
	ButtonWithSpinner,
	FormFieldControl,
	Input,
	ToastCustom,
} from "@components";
import { useMutation } from "@tanstack/react-query";
import { normalizeText } from "@utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { QuestionType } from "@/constants/questionType.enum";
import { OptionDisplayField } from "@/features/questions/components";
import { formatDateToLocalYYYYMMDD } from "@/features/questions/utils/questionValidators";
import { KEY_ANSWER } from "../constants";
import { useChecklistImageUpload } from "../hooks";
import { checklistAnswerSection } from "../services";
import { useChecklistProgressStore, useChecklistStore } from "../store";
import type {
	AnnotatedFile,
	AnnotatedFileWithInput,
	ChecklistAnswerSectionRequest,
	FileUploadStatusValue,
	IAnswer,
	IQuestion,
	Option,
	Question,
	QuestionOptions,
	QuestionSettings,
} from "../types";
import { FileUploadStatus } from "../types";
import { mergeChecklistAnswerResponse } from "../utils/checklist-answer.utils";
import { getUploadUserFacingMessage } from "../utils/checklist-upload-error.utils";
import { IconQuestion, StepHeader } from "./";
import Camera from "./capture-photo";

function getDefaultOptionIds(question: Question): string[] {
	if (!question.settings.allowDefaultOption) {
		return [];
	}

	return (question.options ?? [])
		.filter((option) => option.isDefault)
		.map((option) => String(option.id));
}

function getOptionAnswerDefault(
	question: Question,
	isMultiple: boolean
): string | string[] | undefined {
	const recoveredIds = question.answer?.selectedOptionIds?.map(String) ?? [];
	const selectedIds =
		recoveredIds.length > 0 ? recoveredIds : getDefaultOptionIds(question);

	if (isMultiple) {
		return selectedIds;
	}

	return selectedIds[0];
}

function requiredOptionRule(isRequired: boolean) {
	return {
		validate: (value: unknown) => {
			if (!isRequired) {
				return true;
			}
			if (Array.isArray(value)) {
				return value.length > 0 || "Informação obrigatória.";
			}
			if (value === undefined || value === null || value === "") {
				return "Informação obrigatória.";
			}
			return true;
		},
	};
}

const DateRangeType = {
	Any: 0,
	PastOnly: 1,
	FutureOnly: 2,
	Range: 3,
} as const;

function parseDateNoon(dateStr: string): Date {
	const normalized = dateStr.includes("T") ? dateStr : `${dateStr}T12:00:00`;
	const d = new Date(normalized);
	d.setHours(12, 0, 0, 0);
	return d;
}

function validateDateRangeInterval(
	date: Date,
	minStr: string | null,
	maxStr: string | null
): true | string {
	const hasMin = !!minStr;
	const hasMax = !!maxStr;
	const hasNoBounds = !(hasMin || hasMax);
	if (hasNoBounds) {
		return true;
	}

	const minDate = hasMin ? parseDateNoon(minStr as string) : null;
	const maxDate = hasMax ? parseDateNoon(maxStr as string) : null;

	// Quando ambos os limites existem, usamos mensagem única no formato:
	// "A data deve estar entre dd/mm/aaaa e dd/mm/aaaa."
	if (minDate && maxDate) {
		if (date < minDate || date > maxDate) {
			const minStrPt = minDate.toLocaleDateString("pt-BR");
			const maxStrPt = maxDate.toLocaleDateString("pt-BR");
			return `A data deve estar entre ${minStrPt} e ${maxStrPt}.`;
		}
		return true;
	}

	if (minDate && date < minDate) {
		return `A data deve ser posterior ou igual a ${minDate.toLocaleDateString("pt-BR")}.`;
	}

	if (maxDate && date > maxDate) {
		return `A data deve ser anterior ou igual a ${maxDate.toLocaleDateString("pt-BR")}.`;
	}

	return true;
}

function validateDateByRule(
	value: string,
	settings: QuestionSettings | undefined
): true | string {
	if (!value) {
		return true;
	}
	if (!settings) {
		return true;
	}
	const rangeType = Number(settings.dateRangeType);
	if (Number.isNaN(rangeType)) {
		return true;
	}
	if (rangeType === DateRangeType.Any) {
		return true;
	}
	const date = parseDateNoon(value);
	const today = new Date();
	today.setHours(12, 0, 0, 0);

	if (rangeType === DateRangeType.PastOnly) {
		return date > today ? "Apenas datas no passado são permitidas." : true;
	}
	if (rangeType === DateRangeType.FutureOnly) {
		return date < today ? "Apenas datas no futuro são permitidas." : true;
	}
	if (rangeType === DateRangeType.Range) {
		return validateDateRangeInterval(date, settings.minDate, settings.maxDate);
	}
	return true;
}

function getNumberRangeMessage(
	minVal: number | undefined | null,
	maxVal: number | undefined | null
): string {
	if (
		minVal !== undefined &&
		minVal !== null &&
		maxVal !== undefined &&
		maxVal !== null
	) {
		return `O valor deve ser maior ou igual a ${minVal} e menor ou igual a ${maxVal}.`;
	}
	if (minVal !== undefined && minVal !== null) {
		return `O valor deve ser maior ou igual a ${minVal}.`;
	}
	if (maxVal !== undefined && maxVal !== null) {
		return `O valor deve ser menor ou igual a ${maxVal}.`;
	}
	return "";
}

/** Detecta celular/tablet (touch + tela estreita) para mostrar campo de data/hora como texto com placeholder. */
function useIsMobileOrTablet(): boolean {
	const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
	useEffect(() => {
		const check = () => {
			const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
			const isNarrow = window.innerWidth < 768;
			setIsMobileOrTablet(hasTouch && isNarrow);
		};
		check();
		window.addEventListener("resize", check);
		return () => window.removeEventListener("resize", check);
	}, []);
	return isMobileOrTablet;
}

/** Formata YYYY-MM-DD para exibição dd/mm/aaaa. */
function formatDateToDdMmYyyy(yyyyMmDd: string): string {
	if (!yyyyMmDd) {
		return "";
	}
	const [y, m, d] = yyyyMmDd.split("-");
	if (!(y && m && d)) {
		return "";
	}
	return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
}

/** Normaliza valor de hora para HH:mm para input type="time" e exibição. */
function normalizeTimeValue(value: string): string {
	if (!value) {
		return "";
	}
	const part = value.includes("T") ? value.split("T")[1] : value;
	return part.slice(0, 5); // HH:mm
}

function isUploadableBlob(
	file: AnnotatedFile | undefined
): file is AnnotatedFile & Blob {
	return file instanceof Blob && file.size > 0;
}

function withFileUploadStatus(
	file: AnnotatedFile,
	status: {
		uploadStatus: FileUploadStatusValue;
		uploaded?: boolean;
		fileKey?: string;
		uploadErrorMessage?: string;
	}
): AnnotatedFile {
	return Object.assign(file, status);
}

function buildFileQuestion(
	question: Question,
	value: unknown,
	questions: IQuestion[]
): boolean {
	const valueObj = value as AnnotatedFileWithInput | undefined;
	const file = valueObj?.file;

	if (!file) {
		questions.push({
			checklistAnswerQuestionId: question.checklistAnswerQuestionId,
			valueFiles: [],
		});
		return true;
	}

	const fileKey = file.fileKey ?? (file.uploaded ? file.name : undefined);
	if (!fileKey) {
		return false;
	}

	questions.push({
		checklistAnswerQuestionId: question.checklistAnswerQuestionId,
		valueFiles: [
			{
				fileKey,
				description: valueObj?.description || "",
				damages: file.damages || [],
			},
		],
	});

	return true;
}

/** Retorna min/max para input type="date" para desativar dias fora da regra no picker. */
function getDateInputMinMax(settings: QuestionSettings | undefined): {
	min?: string;
	max?: string;
} {
	if (!settings) {
		return {};
	}
	const rangeType = Number(settings.dateRangeType);
	if (Number.isNaN(rangeType)) {
		return {};
	}
	const today = new Date();
	today.setHours(12, 0, 0, 0);
	const todayStr = formatDateToLocalYYYYMMDD(today);

	if (rangeType === DateRangeType.Any) {
		return {};
	}
	if (rangeType === DateRangeType.PastOnly) {
		return { max: todayStr };
	}
	if (rangeType === DateRangeType.FutureOnly) {
		return { min: todayStr };
	}
	if (rangeType === DateRangeType.Range) {
		const min = settings.minDate
			? formatDateToLocalYYYYMMDD(parseDateNoon(settings.minDate))
			: undefined;
		const max = settings.maxDate
			? formatDateToLocalYYYYMMDD(parseDateNoon(settings.maxDate))
			: undefined;
		return { min, max };
	}
	return {};
}

export const QuestionStep = () => {
	const nextStep = useChecklistProgressStore((state) => state.nextStep);
	const prevStep = useChecklistProgressStore((state) => state.prevStep);
	const checklist = useChecklistStore((state) => state.checklist);
	const { answerData } = checklist || {};
	const setChecklist = useChecklistStore((state) => state.setChecklist);
	const currentSection = useChecklistProgressStore(
		(state) => state.currentSection
	);
	const clearChecklist = useChecklistStore((state) => state.clearChecklist);
	const setInitialState = useChecklistProgressStore(
		(state) => state.setInitialState
	);

	const isMobileOrTablet = useIsMobileOrTablet();
	const checklistAnswer = answerData?.checklistAnswer;
	const { name: titlePage, description: sectionDescription } =
		checklistAnswer?.sections[currentSection] || {};
	const showOnboarding = checklistAnswer?.sections[
		currentSection
	]?.questions?.some((q) => q.questionType === "File");

	const { mutate, isPending } = useMutation({
		mutationKey: ["checklist:answer-section"],
		mutationFn: checklistAnswerSection,
		onSuccess: (data) => {
			if (!checklist?.answerData) {
				return;
			}

			setChecklist({
				linkData: checklist.linkData,
				answerData: mergeChecklistAnswerResponse(checklist.answerData, data),
			});
			nextStep();
		},
		onError: (error) => {
			if (
				error?.message ===
					"O checklist expirou e não pode mais ser respondido." ||
				error?.message === "A resposta do checklist não está em andamento."
			) {
				// Reseto os steps e o checklist e levo ele pra o início
				toast(<ToastCustom description={error?.message} title="Erro" />, {
					onDismiss: () => {
						setInitialState();
						clearChecklist();
					},
					onAutoClose: () => {
						setInitialState();
						clearChecklist();
					},
				});
			} else if (error?.message) {
				toast(<ToastCustom description={error?.message} title="Erro" />);
			}
		},
	});

	const { uploadImage } = useChecklistImageUpload();
	const uploadRequestIdRef = useRef<Record<string, number>>({});
	const fileSourceRef = useRef<Record<string, AnnotatedFile>>({});

	const {
		control,
		handleSubmit,
		getValues,
		watch,
		formState: { errors },
	} = useForm({
		mode: "onSubmit",
		disabled: isPending,
	});

	const watchedValues = watch();

	const isUploadBlocking = useMemo(() => {
		const fileQuestions =
			checklistAnswer?.sections[currentSection]?.questions?.filter(
				(q) => q.questionType === "File"
			) ?? [];

		return fileQuestions.some((question) => {
			const value = watchedValues[String(question.questionId)] as
				| AnnotatedFileWithInput
				| undefined;
			const file = value?.file;

			if (!file) {
				return false;
			}

			if (file.uploaded) {
				return false;
			}

			return (
				file.uploadStatus === FileUploadStatus.Uploading ||
				file.uploadStatus === FileUploadStatus.Error
			);
		});
	}, [watchedValues, checklistAnswer, currentSection]);

	const resolveUploadFile = useCallback(
		(
			questionId: string,
			currentValue: AnnotatedFileWithInput
		): AnnotatedFile | undefined => {
			const storedFile = fileSourceRef.current[questionId];
			if (isUploadableBlob(storedFile)) {
				return storedFile;
			}

			const formFile = currentValue.file;
			if (isUploadableBlob(formFile)) {
				return formFile;
			}

			return storedFile ?? formFile;
		},
		[]
	);

	const uploadChecklistImage = useCallback(
		async (
			questionId: string,
			questionAnswerId: number,
			currentValue: AnnotatedFileWithInput,
			onChange: (value: AnnotatedFileWithInput) => void
		) => {
			const file = resolveUploadFile(questionId, currentValue);
			if (!(file?.name && file?.type)) {
				return;
			}

			if (!isUploadableBlob(file)) {
				return;
			}

			if (!(checklistAnswer?.id && checklistAnswer?.answerToken)) {
				return;
			}

			fileSourceRef.current[questionId] = file;

			const requestId = Date.now();
			uploadRequestIdRef.current[questionId] = requestId;

			onChange({
				...currentValue,
				file: withFileUploadStatus(file, {
					uploadStatus: FileUploadStatus.Uploading,
					uploaded: false,
					fileKey: undefined,
					uploadErrorMessage: undefined,
				}),
			});

			try {
				const { fileKey } = await uploadImage({
					file,
					questionAnswerId,
					answerId: checklistAnswer.id,
					answerToken: checklistAnswer.answerToken,
				});

				if (uploadRequestIdRef.current[questionId] !== requestId) {
					return;
				}

				onChange({
					...currentValue,
					file: withFileUploadStatus(file, {
						uploadStatus: FileUploadStatus.Success,
						fileKey,
						uploaded: false,
						uploadErrorMessage: undefined,
					}),
				});
			} catch (error) {
				if (uploadRequestIdRef.current[questionId] !== requestId) {
					return;
				}

				onChange({
					...currentValue,
					file: withFileUploadStatus(file, {
						uploadStatus: FileUploadStatus.Error,
						uploaded: false,
						fileKey: undefined,
						uploadErrorMessage: getUploadUserFacingMessage(error),
					}),
				});
			}
		},
		[checklistAnswer, resolveUploadFile, uploadImage]
	);

	const formatData = (
		value: string,
		questionType: QuestionOptions,
		checklistAnswerQuestionId: number
	) => {
		const { field, transform } = KEY_ANSWER[questionType];

		if (questionType === "Number") {
			return {
				checklistAnswerQuestionId,
				valueNumber: transform(value) as number | null,
			};
		}

		const isValid =
			value !== undefined &&
			value !== null &&
			value !== "undefined" &&
			value !== "null";

		return {
			checklistAnswerQuestionId,
			...(isValid && {
				[field]: transform(value),
			}),
		};
	};

	const processQuestion = (
		q: Question,
		data: Record<string, string>,
		questions: IQuestion[]
	): boolean => {
		const value = data[q.questionId];
		if (!(value || KEY_ANSWER[q.questionType])) {
			return false;
		}

		if (q.questionType === "File") {
			return buildFileQuestion(q, value, questions);
		}
		questions.push(
			formatData(value, q.questionType, q.checklistAnswerQuestionId)
		);

		return true;
	};

	const buildQuestions = (
		data: Record<string, string>,
		checkListSectionQuestions: Question[]
	): IQuestion[] => {
		const questions: IQuestion[] = [];

		for (const q of checkListSectionQuestions) {
			if (!processQuestion(q, data, questions)) {
				return questions;
			}
		}

		return questions;
	};

	const onSubmit = (data: Record<string, string>) => {
		if (
			isUploadBlocking ||
			!(checklistAnswer?.id && checklistAnswer?.answerToken)
		) {
			return;
		}

		const questions = buildQuestions(
			data,
			checklistAnswer?.sections[currentSection]?.questions || []
		);

		const payload: ChecklistAnswerSectionRequest = {
			answerId: String(checklistAnswer.id),
			answerToken: checklistAnswer.answerToken,
			questions,
		};

		mutate(payload);
	};

	const mountDefaultValues = (
		answer?: IAnswer,
		options?: Option[]
	): string | AnnotatedFileWithInput | undefined => {
		if (!answer) {
			return;
		}

		if (answer.files?.length) {
			const f = answer.files[0];
			return {
				file: {
					...(f as AnnotatedFile),
					name: f.fileKey,
					fileKey: f.fileKey,
					type: f.fileUrl.split(".").pop()
						? `image/${f.fileUrl.split(".").pop()}`
						: "image/jpeg",
					uri: f.fileUrl,
					uploaded: true,
					uploadStatus: FileUploadStatus.Success,
				},
				description: f.description || "",
			};
		}

		if (answer.selectedOptionIds?.length) {
			return (
				answer.selectedOptionIds[0]?.toString() ||
				options?.find((o) => o.isDefault)?.id?.toString() ||
				""
			);
		}

		return answer.text || "";
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: is not necessary to re-run this effect when the currentSection changes
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}, [currentSection]);

	return (
		<form
			className="mx-auto flex min-h-dvh w-full flex-col gap-5 bg-secondary pt-5"
			onSubmit={handleSubmit(onSubmit)}
		>
			<StepHeader
				hasFileUpload={showOnboarding}
				subtitle={sectionDescription || undefined}
				title={titlePage}
			/>

			{checklistAnswer?.sections[currentSection]?.questions.map((question) => {
				const fieldName = `${question.questionId}`;
				// API pode retornar isRequired (camelCase) ou IsRequired (PascalCase); default false = não obrigatório
				const isRequired = Boolean(
					question.isRequired ??
						(question as { IsRequired?: boolean }).IsRequired ??
						false
				);

				const defaultValue = mountDefaultValues(question?.answer || undefined);

				return (
					<div
						className="group mx-auto flex h-full w-full flex-col items-center gap-4 px-4"
						key={question.questionId}
					>
						{/* TEXTO  */}
						{question.questionType === QuestionType[5] && (
							<Controller
								control={control}
								defaultValue={question.answer?.text || ""}
								name={fieldName}
								render={({ field: { value, onChange, disabled } }) => (
									<FormFieldControl
										className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
										error={errors[fieldName]?.message as string}
										hint={question.description}
										htmlFor={fieldName}
										isRequired={isRequired}
										label={question.prompt}
									>
										<Input
											disabled={disabled}
											error={!!errors[fieldName]?.message}
											maxLength={question?.settings?.maxLength || 0}
											onChange={onChange}
											placeholder="Digite aqui..."
											value={value}
										/>
									</FormFieldControl>
								)}
								rules={{
									required: {
										value: isRequired,
										message: "Informação obrigatória.",
									},
									...(Number(question?.settings?.minLength) > 0
										? {
												minLength: {
													value: question.settings.minLength as number,
													message: `Mínimo de ${question.settings.minLength} caracteres.`,
												},
											}
										: {}),
									...(Number(question?.settings?.maxLength) > 0
										? {
												maxLength: {
													value: question.settings.maxLength as number,
													message: `Máximo de ${question.settings.maxLength} caracteres.`,
												},
											}
										: {}),
								}}
							/>
						)}

						{question.questionType === QuestionType[1] && (
							<>
								{/* IconQuestion */}
								{question.settings.optionDisplayType === 1 && (
									<Controller
										control={control}
										defaultValue={getOptionAnswerDefault(question, false)}
										name={fieldName}
										render={({ field }) => (
											<FormFieldControl
												className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
												error={errors[fieldName]?.message as string}
												hint={question.description}
												htmlFor={fieldName}
												isRequired={isRequired}
												label={question.prompt}
												showIconError={false}
											>
												<IconQuestion
													error={!!errors[fieldName]?.message}
													onChange={field.onChange}
													question={question}
													value={field.value}
												/>
											</FormFieldControl>
										)}
										rules={{
											required: {
												value: isRequired,
												message: "Informação obrigatória.",
											},
										}}
									/>
								)}
								{/* Checkbox / Radio / ComboBox / List */}
								{(question.settings.optionDisplayType === 2 ||
									question.settings.optionDisplayType === 3 ||
									question.settings.optionDisplayType === 4 ||
									question.settings.optionDisplayType === 5) && (
									<Controller
										control={control}
										defaultValue={getOptionAnswerDefault(
											question,
											Boolean(question.settings.isMultipleSelection)
										)}
										name={fieldName}
										render={({ field: { value, onChange, disabled } }) => (
											<FormFieldControl
												className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
												error={errors[fieldName]?.message as string}
												hint={question.description}
												htmlFor={fieldName}
												isRequired={isRequired}
												label={question.prompt}
												showIconError={
													question.settings.optionDisplayType === 4
												}
											>
												<OptionDisplayField
													allowDeselect={!isRequired}
													ariaLabel={question.prompt}
													isDisabled={disabled}
													isInvalid={!!errors[fieldName]?.message}
													isMultipleSelection={Boolean(
														question.settings.isMultipleSelection
													)}
													onChange={onChange}
													optionDisplayType={
														question.settings.optionDisplayType
													}
													options={question.options ?? []}
													value={value}
												/>
											</FormFieldControl>
										)}
										rules={requiredOptionRule(isRequired)}
									/>
								)}
							</>
						)}

						{/* DATE */}
						{question.questionType === QuestionType[2] && (
							<Controller
								control={control}
								defaultValue={
									question.answer?.dateTimeValue?.split("T")[0] || ""
								}
								name={fieldName}
								render={({ field: { value, onChange, disabled } }) => {
									const { min, max } = getDateInputMinMax(question.settings);
									if (isMobileOrTablet) {
										return (
											<FormFieldControl
												className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
												error={errors[fieldName]?.message as string}
												hint={question.description}
												htmlFor={fieldName}
												isRequired={isRequired}
												label={question.prompt}
												showIconError={false}
											>
												<div className="relative">
													<Input
														className="pointer-events-none"
														disabled={disabled}
														error={!!errors[fieldName]?.message}
														hideTrailingErrorIcon
														placeholder="dd/mm/aaaa"
														readOnly
														type="text"
														value={value ? formatDateToDdMmYyyy(value) : ""}
													/>
													{!disabled && (
														<input
															aria-label={question.prompt}
															className="absolute inset-0 z-10 cursor-pointer opacity-0"
															max={max}
															min={min}
															onChange={(e) => onChange(e.target.value)}
															title={question.prompt}
															type="date"
															value={value || ""}
														/>
													)}
												</div>
											</FormFieldControl>
										);
									}
									return (
										<FormFieldControl
											className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
											error={errors[fieldName]?.message as string}
											hint={question.description}
											htmlFor={fieldName}
											isRequired={isRequired}
											label={question.prompt}
											showIconError={false}
										>
											<div className="relative flex w-full cursor-pointer">
												<Input
													disabled={disabled}
													error={!!errors[fieldName]?.message}
													hideTrailingErrorIcon
													max={max}
													min={min}
													name={fieldName}
													onChange={onChange}
													type="date"
													value={value}
												/>
												{!disabled && (
													<button
														aria-label="Abrir calendário"
														className="absolute inset-0 z-10 cursor-pointer rounded-lg"
														onClick={() => {
															document
																.querySelector<HTMLInputElement>(
																	`input[name="${fieldName}"][type="date"]`
																)
																?.showPicker?.();
														}}
														title="Abrir calendário"
														type="button"
													/>
												)}
											</div>
										</FormFieldControl>
									);
								}}
								rules={{
									required: {
										value: isRequired,
										message: "Informação obrigatória.",
									},
									validate: (v) =>
										validateDateByRule(v as string, question.settings),
								}}
							/>
						)}

						{/* TIME */}
						{question.questionType === QuestionType[3] && (
							<Controller
								control={control}
								defaultValue={question.answer?.dateTimeValue || ""}
								name={fieldName}
								render={({ field: { value, onChange, disabled } }) => {
									const timeDisplay = normalizeTimeValue(value);
									if (isMobileOrTablet) {
										return (
											<FormFieldControl
												className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
												error={errors[fieldName]?.message as string}
												hint={question.description}
												htmlFor={fieldName}
												isRequired={isRequired}
												label={question.prompt}
												showIconError={false}
											>
												<div className="relative">
													<Input
														className="pointer-events-none"
														disabled={disabled}
														error={!!errors[fieldName]?.message}
														hideTrailingErrorIcon
														placeholder="--:--"
														readOnly
														type="text"
														value={timeDisplay}
													/>
													{!disabled && (
														<input
															aria-label={question.prompt}
															className="absolute inset-0 z-10 cursor-pointer opacity-0"
															onChange={(e) => onChange(e.target.value)}
															title={question.prompt}
															type="time"
															value={timeDisplay}
														/>
													)}
												</div>
											</FormFieldControl>
										);
									}
									return (
										<FormFieldControl
											className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
											error={errors[fieldName]?.message as string}
											hint={question.description}
											htmlFor={fieldName}
											isRequired={isRequired}
											label={question.prompt}
											showIconError={false}
										>
											<div className="relative flex w-full cursor-pointer">
												<Input
													disabled={disabled}
													error={!!errors[fieldName]?.message}
													hideTrailingErrorIcon
													name={fieldName}
													onChange={onChange}
													type="time"
													value={value}
												/>
												{!disabled && (
													<button
														aria-label="Abrir seletor de hora"
														className="absolute inset-0 z-10 cursor-pointer rounded-lg"
														onClick={() => {
															document
																.querySelector<HTMLInputElement>(
																	`input[name="${fieldName}"][type="time"]`
																)
																?.showPicker?.();
														}}
														title="Abrir seletor de hora"
														type="button"
													/>
												)}
											</div>
										</FormFieldControl>
									);
								}}
								rules={{
									required: {
										value: isRequired,
										message: "Informação obrigatória.",
									},
								}}
							/>
						)}

						{/* NUMBER */}
						{question.questionType === QuestionType[6] && (
							<Controller
								control={control}
								defaultValue={question.answer?.number?.toString() || ""}
								name={fieldName}
								render={({ field: { value, onChange, disabled } }) => (
									<FormFieldControl
										className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
										error={errors[fieldName]?.message as string}
										hint={question.description}
										htmlFor={fieldName}
										isRequired={isRequired}
										label={question.prompt}
									>
										<Input
											disabled={disabled}
											error={!!errors[fieldName]?.message}
											max={question?.settings?.maxValue ?? undefined}
											min={question?.settings?.minValue ?? undefined}
											onChange={onChange}
											placeholder="Digite o número..."
											type="number"
											value={value}
										/>
									</FormFieldControl>
								)}
								rules={{
									required: {
										value: isRequired,
										message: "Informação obrigatória.",
									},
									validate: (v) => {
										const num = Number(v);
										if (v === "" || Number.isNaN(num)) {
											return true;
										}
										const minVal = question?.settings?.minValue;
										const maxVal = question?.settings?.maxValue;
										const msg = getNumberRangeMessage(minVal, maxVal);
										if (!msg) {
											return true;
										}
										if (
											minVal !== undefined &&
											minVal !== null &&
											num < minVal
										) {
											return msg;
										}
										if (
											maxVal !== undefined &&
											maxVal !== null &&
											num > maxVal
										) {
											return msg;
										}
										return true;
									},
								}}
							/>
						)}

						{/* FOTOS/Files */}
						{question.questionType === QuestionType[7] && (
							<>
								<Controller
									control={control}
									defaultValue={defaultValue ? defaultValue : null}
									name={fieldName}
									render={({ field: { value, onChange } }) => (
										<FormFieldControl
											className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
											error={errors[fieldName]?.message as string}
											hint={question.description}
											htmlFor={fieldName}
											isRequired={isRequired}
											label={question.prompt}
											showIconError={false}
										>
											<Camera
												allowDamageReport={
													question?.settings?.allowDamageReport
												}
												error={!!errors[fieldName]?.message}
												fileName={`${normalizeText(
													question.prompt
												)}-${question.checklistAnswerQuestionId}.png`}
												isRequired={isRequired}
												onCapture={(file: AnnotatedFile) => {
													fileSourceRef.current[fieldName] = file;
													const nextValue: AnnotatedFileWithInput = {
														...(value || {}),
														file,
													};
													onChange(nextValue);
													uploadChecklistImage(
														fieldName,
														question.checklistAnswerQuestionId,
														nextValue,
														onChange
													);
												}}
												onRemove={
													isRequired
														? undefined
														: () => {
																uploadRequestIdRef.current[fieldName] =
																	Date.now();
																delete fileSourceRef.current[fieldName];
																onChange(null);
															}
												}
												onRetry={(retryFile) => {
													if (retryFile) {
														fileSourceRef.current[fieldName] = retryFile;
													}
													const currentValue =
														(getValues(fieldName) as AnnotatedFileWithInput) ||
														(value as AnnotatedFileWithInput) ||
														{};
													uploadChecklistImage(
														fieldName,
														question.checklistAnswerQuestionId,
														currentValue,
														onChange
													);
												}}
												uploadErrorMessage={
													(value as AnnotatedFileWithInput)?.file
														?.uploadErrorMessage
												}
												uploadStatus={
													(value as AnnotatedFileWithInput)?.file?.uploadStatus
												}
												value={(value as AnnotatedFileWithInput)?.file}
											/>
										</FormFieldControl>
									)}
									rules={{
										required: {
											value: isRequired,
											message: "Informação obrigatória.",
										},
									}}
								/>
								<Controller
									control={control}
									name={fieldName}
									render={({ field: { value, onChange, disabled } }) => (
										<FormFieldControl
											className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
											htmlFor={fieldName}
											label="Legenda"
										>
											<Input
												className="max-w-80"
												disabled={
													disabled ||
													!(value as AnnotatedFileWithInput)?.file?.name
												}
												maxLength={250}
												onChange={({ target }) => {
													onChange({
														...(value || {}),
														description: target.value,
													});
												}}
												placeholder="Digite aqui..."
												value={
													(value as AnnotatedFileWithInput)?.description ?? ""
												}
											/>
										</FormFieldControl>
									)}
								/>
							</>
						)}

						{/* BOOLEAN - Sim/Não */}
						{question.questionType === QuestionType[8] && (
							<Controller
								control={control}
								defaultValue={
									typeof question.answer?.boolean === "boolean"
										? question.answer.boolean
										: false
								}
								name={fieldName}
								render={({ field: { value, onChange, disabled } }) => (
									<FormFieldControl
										className="max-w-[456px] group-[:not(:last-child,:nth-last-child(2))]:border-secondary group-[:not(:last-child,:nth-last-child(2))]:border-b group-[:not(:last-child,:nth-last-child(2))]:pb-6"
										error={errors[fieldName]?.message as string}
										hint={question.description}
										htmlFor={fieldName}
										isRequired={false}
										label=""
										showIconError={false}
									>
										<Checkbox
											isDisabled={disabled}
											isSelected={!!value}
											label={
												<span className="inline-flex items-center gap-1">
													{question.prompt}
													{!!isRequired && (
														<span className="text-brand-tertiary">*</span>
													)}
												</span>
											}
											onChange={onChange}
										/>
									</FormFieldControl>
								)}
								rules={{
									required: {
										value: isRequired,
										message: "Informação obrigatória.",
									},
								}}
							/>
						)}
					</div>
				);
			})}

			<div className="sticky bottom-0 left-0 mt-auto w-full border-primary border-t bg-secondary px-4 pt-4 pb-8 text-center">
				<div className="mx-auto flex w-full max-w-[456px] gap-2">
					<Button
						className="flex-1/2"
						disabled={isPending}
						onClick={(e) => {
							e.preventDefault();
							prevStep();
						}}
						type="button"
						variant="secondary"
					>
						Voltar
					</Button>
					<ButtonWithSpinner
						className="flex-1/2"
						disabled={isPending || isUploadBlocking}
						isLoading={isPending}
					>
						Continuar
					</ButtonWithSpinner>
				</div>
			</div>
		</form>
	);
};
